// Pure script builder for the voice coach. The TIMER owns timing; these events only say what to speak when.
const NUM = ['Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve',
  'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen', 'Twenty', 'Twenty-one', 'Twenty-two',
  'Twenty-three', 'Twenty-four', 'Twenty-five', 'Twenty-six', 'Twenty-seven', 'Twenty-eight', 'Twenty-nine', 'Thirty'];
export const numWord = (n) => NUM[n] || String(n);
export const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
export const LEAD_SECS = 3;

const LOWER_FIRST = /squat|lunge|push.?up|press.?up|bench|dip|split|step.?down|chest press|floor press|sit.?up|crunch/;
const LIFT_FIRST = /row|curl|pull|chin|raise|fly|deadlift|rdl|thrust|bridge|shrug|extension|kickback|press|face|lift/;
const BALLISTIC = /swing|jump|slam|snatch|clean|burpee|skater|thruster|jack|mountain climber|high knee/;

export function exerciseName(item, ex) { return ex?.name || item?.exercise_name || 'Exercise'; }

// Approximate time the coach needs to say `text` at the chosen speaking speed (never touches exercise tempo).
export const speechSecs = (text, rate = 1) => Math.ceil((String(text).split(/\s+/).length / 2.6) / (rate || 1) + 1.5);

export function resolveTempo(item, ex, { circuit = false } = {}) {
  const name = exerciseName(item, ex).toLowerCase();
  const raw = String(item?.tempo || ex?.tempo || '').toLowerCase().trim();
  if (item?.target_unit === 'open' || (circuit && !item?.target_reps)) return { kind: 'open', label: 'Your pace · tap done when finished' };
  if (item?.target_unit === 'seconds' || /isometric|hold|plank/.test(raw) || /plank|wall sit|hold/.test(name)) {
    return { kind: 'timed', label: circuit ? 'Timed work' : 'Timed hold' };
  }
  if (BALLISTIC.test(name) || /explosive|fast|ballistic|swing/.test(raw) || (circuit && !/\d/.test(raw))) {
    return { kind: 'ballistic', repSecs: 2, label: circuit ? 'Steady rhythm · 1 rep every 2s' : 'Explosive rhythm · 1 rep every 2s' };
  }
  const nums = (raw.match(/\d+(\.\d+)?/g) || []).map(Number);
  let [E, P, C, P2] = [3, 0, 1, 0];
  if (nums.length === 2) [E, C] = nums;
  else if (nums.length >= 3) [E, P, C, P2 = 0] = nums;
  E = Math.max(1, E); C = Math.max(1, C);
  const lowerFirst = LOWER_FIRST.test(name) || !LIFT_FIRST.test(name);
  let down = 'Down', up = 'Up', hold = 'Hold';
  if (lowerFirst) up = /push|bench|dip|chest|floor press/.test(name) ? 'Push' : /squat|lunge|split|step/.test(name) ? 'Drive up' : 'Up';
  else {
    down = 'Lower'; hold = 'Squeeze';
    up = /row|pull|chin|face/.test(name) ? 'Pull' : /curl/.test(name) ? 'Curl' : /press/.test(name) ? 'Press' : /deadlift|rdl|thrust|bridge/.test(name) ? 'Drive up' : 'Lift';
  }
  const ecc = { key: 'ecc', cue: down, secs: E, count: true };
  const con = { key: 'con', cue: up, secs: C, count: C >= 2 };
  const pause = { key: 'hold', cue: hold, secs: P, count: false };
  const phases = (lowerFirst ? [ecc, pause, con] : [con, pause, ecc]).filter(p => p.secs > 0);
  if (P2 > 0) phases.push({ key: 'reset', cue: '', secs: P2, count: false });
  const repSecs = phases.reduce((s, p) => s + p.secs, 0);
  return { kind: 'tempo', phases, repSecs, label: `${E}s ${down.toLowerCase()}${P ? ` · ${P}s ${hold.toLowerCase()}` : ''} · ${C}s ${up.toLowerCase()}`, firstCue: phases[0].cue };
}

const COMPLETE = [
  (n) => `Excellent. That's ${n} repetitions. You've completed your set.`,
  (n) => `Brilliant. ${n} reps, set complete.`,
  (n) => `Great work. That's all ${n}. Set done.`,
  (n) => `Nice job. ${n} repetitions, that's your set.`,
];
const STATION_DONE = ['Great work. Station done.', 'Nice. That\'s the station.', 'Done. Brilliant effort.'];
const TWO_MORE = ['Two more. You\'ve got this.', 'Two left. Stay with me.', 'Just two more. Strong finish.'];
const NEXT_SET = ['Ready? Let\'s go for the next set.', 'Here we go. Next set.', 'Ready? Let\'s go again.'];
const OPEN_PUSH = ['Keep moving. Strong and steady.', 'Good pace. Stay with it.', 'Keep breathing. You\'re doing great.'];
const technique = (name) => {
  if (/row|deadlift|rdl|hinge|good morning|swing/.test(name)) return ['Keep that back strong.', 'Brace your core.', 'Flat back, nice and controlled.'];
  if (/squat|lunge|split/.test(name)) return ['Chest up.', 'Knees track over your toes.', 'Stay tall through your chest.'];
  if (/push|bench|press/.test(name)) return ['Elbows tucked.', 'Keep your core tight.', 'Smooth and controlled.'];
  return ['Good control. Stay focused.', 'Nice and smooth.', 'Keep breathing.'];
};

// Spoken events for ONE set / station: [{t, text, mode}] where t = seconds from its start.
export function buildSetEvents(tempo, target, { first, motivation, name, style = 'strength' }) {
  const circuit = style !== 'strength';
  const lead = circuit ? `${name}. Ready? Let's go.` : first ? 'Ready? Let\'s go.' : pick(NEXT_SET);
  const ev = [{ t: 0, text: lead, mode: 'interrupt' }];
  const n = Math.max(1, target || 1);
  if (tempo.kind === 'open') {
    ev.push({ t: LEAD_SECS, text: 'Go. Tap done when you\'ve finished this station.', mode: 'interrupt' });
    for (let k = 1; k <= 20; k++) ev.push({ t: LEAD_SECS + k * 45, text: OPEN_PUSH[k % OPEN_PUSH.length], mode: 'ifIdle', opt: true });
    return { events: ev, duration: Infinity };
  }
  if (tempo.kind === 'timed') {
    const d = n, s = LEAD_SECS;
    ev.push({ t: s, text: circuit ? 'Go. Work hard.' : 'Go. Hold it strong.', mode: 'interrupt' });
    if (d >= 20) ev.push({ t: s + Math.floor(d / 2), text: circuit ? 'Halfway. Keep pushing.' : 'Halfway. Keep going.', mode: 'ifIdle' });
    if (d >= 20) ev.push({ t: s + d - 10, text: 'Ten seconds.', mode: 'interrupt' });
    [3, 2, 1].forEach(k => d > k && ev.push({ t: s + d - k, text: `${numWord(k)}.`, mode: 'interrupt' }));
    ev.push({ t: s + d, text: circuit ? `Time. ${pick(STATION_DONE)}` : 'Time. Great work, set complete.', mode: 'interrupt' });
    return { events: ev, duration: s + d };
  }
  const tech = technique(name.toLowerCase());
  const mid = Math.floor(n / 2);
  // Extra words for rep k. "Last one" is always said; the rest are motivation (mutable at runtime via `plain`).
  const extra = (k) => {
    if (k === n) return { say: 'Last one. Stay strong.', must: true };
    if (motivation && n >= 6 && k === n - 1) return { say: pick(TWO_MORE) };
    if (motivation && n >= 6 && k === mid + 1) return { say: pick(tech) };
    return null;
  };
  const withExtra = (base, x) => ({ text: x ? `${base} ${x.say}`.trim() : base, plain: x && !x.must ? base : undefined });
  if (tempo.kind === 'ballistic') {
    ev.push({ t: LEAD_SECS, text: 'Go.', mode: 'interrupt' });
    for (let k = 1; k < n; k++) ev.push({ t: LEAD_SECS + k * tempo.repSecs, mode: 'interrupt', ...withExtra(`${numWord(k)}.`, extra(k + 1)) });
  } else {
    // Long phrases ride on the slowest phase so they never get cut off by the next movement cue.
    const L = tempo.phases.reduce((b, p, i) => (p.secs > tempo.phases[b].secs ? i : b), 0);
    for (let k = 1; k <= n; k++) {
      let o = LEAD_SECS + (k - 1) * tempo.repSecs;
      const x = extra(k);
      tempo.phases.forEach((p, pi) => {
        if (p.cue) {
          const base = pi === 0 && k > 1 ? `${numWord(k - 1)}. ${p.cue}.` : `${p.cue}.`;
          ev.push({ t: o, mode: 'interrupt', ...withExtra(base, pi === L ? x : null) });
        }
        if (p.count) for (let s = 2; s <= p.secs; s++) ev.push({ t: o + s - 1, text: `${numWord(s).toLowerCase()}.`, mode: 'ifIdle' });
        o += p.secs;
      });
    }
  }
  const duration = LEAD_SECS + n * (tempo.repSecs || 2);
  ev.push({ t: duration, text: `${numWord(n)}. ${circuit ? pick(STATION_DONE) : pick(COMPLETE)(n)}`, mode: 'interrupt' });
  return { events: ev, duration };
}

export const fmtRest = (r) => (r % 60 === 0 ? `${r / 60} minute${r === 60 ? '' : 's'}` : r > 60 ? `${Math.floor(r / 60)} minute${r >= 120 ? 's' : ''} ${r % 60} seconds` : `${r} seconds`);
const REST_OPEN = ['Great set.', 'Nice work.', 'Good set.', 'Well done.'];
const REST_TIPS = ['Relax your shoulders.', 'Take a few deep breaths.', 'Keep your breathing controlled.', 'Shake it out and stay loose.'];

function countdown(ev, rest, tenText) {
  // opt = optional announcement (client can mute); shift = moves later when the client adds rest time.
  if (rest >= 40) ev.push({ t: Math.min(15, Math.floor(rest / 2)), text: pick(REST_TIPS), mode: 'ifIdle', opt: true });
  if (rest >= 50) ev.push({ t: rest - 30, text: 'Thirty seconds remaining.', mode: 'ifIdle', opt: true, shift: true });
  if (rest >= 20) ev.push({ t: rest - 10, text: tenText, mode: 'ifIdle', opt: true, shift: true });
  [3, 2, 1].forEach(k => rest > k + 1 && ev.push({ t: rest - k, text: `${numWord(k)}.`, mode: 'interrupt', opt: true, shift: true }));
  return ev;
}

// Strength rest. In continuous mode the next exercise's instructions (nextSetup) are spoken during the final rest.
export function buildRestEvents(rest, { lastSet, name, nextName, nextSetup }) {
  const head = lastSet
    ? `That's your final set of the ${name}. Great work.${nextName ? ` Next, we're moving to the ${nextName}. Rest for ${fmtRest(rest)}.` : ''}`
    : `${pick(REST_OPEN)} Rest for ${fmtRest(rest)}.`;
  const ev = [{ t: 0.3, text: head, mode: 'queue' }];
  if (nextSetup) ev.push({ t: 0.4, text: nextSetup, mode: 'queue' });
  return countdown(ev, rest, lastSet ? 'Ten seconds. Grab your weights and get ready.' : 'Ten seconds. Get ready for your next set.');
}

// Circuit / Fitness Racing recovery or transition: announces what's next, then counts down to auto-start.
export function buildStationRestEvents(rest, { race, roundEnd, round, nextSetup }) {
  const head = roundEnd ? `Round ${round} complete. Recover for ${fmtRest(rest)}.`
    : race ? `Transition. ${fmtRest(rest)} to get to the next station.` : `Recover. ${fmtRest(rest)}.`;
  const ev = [{ t: 0.3, text: head, mode: 'queue' }];
  if (nextSetup) ev.push({ t: 0.4, text: nextSetup, mode: 'queue' });
  return countdown(ev, rest, race ? 'Ten seconds. Get set at your station.' : 'Ten seconds. Get ready.');
}

const equipLine = (name) => (/dumbbell|\bdb\b/.test(name) ? 'Grab your dumbbells. ' : /barbell/.test(name) ? 'Set up at the barbell. '
  : /kettlebell|\bkb\b/.test(name) ? 'Grab your kettlebell. ' : /band/.test(name) ? 'Grab your band. ' : /cable|machine/.test(name) ? 'Set up at the machine. ' : '');
const libraryHow = (item, ex) => (ex?.voice_setup || '').trim() || [ex?.description, item?.notes || ex?.coach_notes].filter(Boolean).join(' ').trim();

// Starting-position script: coach-written voice_setup wins; otherwise the library instructions verbatim.
export function setupText(item, ex, { first, tempo, continuous = false }) {
  const name = exerciseName(item, ex);
  const how = libraryHow(item, ex);
  const unit = item.target_unit === 'seconds' ? 'seconds' : 'reps';
  const pace = tempo.kind === 'tempo' ? ` Follow my count: ${tempo.label.replace(/ · /g, ', ')}.` : '';
  const heavier = item.load_increase ? ' This week we go a little heavier.' : '';
  const end = continuous ? ' Get into position. I\'ll count you in.' : ' Get into position, and tap ready when you\'re set.';
  return `${first ? 'First up' : 'Next up'}: ${name}. ${equipLine(name.toLowerCase())}${how ? `${how} ` : ''}${item.sets} sets of ${item.target_reps || item.reps} ${unit}.${heavier}${pace}${end}`;
}

// Short station announcement for circuits / racing: name, target and the first library cue.
export function stationSetupText(item, ex, { first, round, station, race }) {
  const name = exerciseName(item, ex);
  const cue = (libraryHow(item, ex).match(/^[^.!?]+[.!?]?/) || [''])[0].trim();
  const target = item.target_unit === 'seconds' ? `${fmtRest(item.target_reps)} of work` : item.target_unit === 'open' || !item.target_reps ? `${item.reps}` : `${item.target_reps} reps`;
  const roundTag = round > 1 && station === 0 ? `Round ${round}. ` : '';
  return `${roundTag}${first ? 'First' : 'Next'} ${race ? 'station' : 'exercise'}: ${name}. ${target}.${cue ? ` ${cue}` : ''}`;
}

const DONE = ['Excellent work. You\'ve completed today\'s strength session.', 'That\'s a wrap. Brilliant session today.', 'Session complete. Really strong work today.'];
const DONE_CIRCUIT = ['Circuit complete. Brilliant work today.', 'That\'s every round done. Great session.'];
const DONE_RACE = ['Race complete. Outstanding effort.', 'That\'s the final station. Brilliant racing today.'];
export const doneText = (kind = 'strength') => pick(kind === 'race' ? DONE_RACE : kind === 'circuit' ? DONE_CIRCUIT : DONE);
